import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Button,
  Link,
  Divider,
  Badge,
  Snackbar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Star as ProIcon,
  Policy as PolicyIcon,
  Gavel as TermsIcon,
  PhotoCamera as PhotoCameraIcon,
  Mic as MicIcon,
  Sensors as NotificationsIcon,
  Person as PersonIcon,
  Analytics as AnalyticsIcon,
  HelpOutline as HelpOutlineIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import {
  HomeIcon,
  DocumentTextIcon,
  BookOpenIcon,
  BeakerIcon,
  HeartIcon,
  UserCircleIcon,
  ChartBarIcon,
  BoltIcon,
  ChatBubbleBottomCenterTextIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import BottomNavigation from './BottomNavigation';
import { useTier } from '../hooks/useTier';
import { Link as RouterLink } from 'react-router-dom';
import { ThemeToggle } from './ThemeToggle';
import { AIBarProvider } from '../contexts/AIBarContext';
import PersistentAIBar from './PersistentAIBar';
import NotificationCenter from './NotificationCenter';
import { useNotificationsContext } from '../contexts/NotificationsContext';
import OfflineBanner from './OfflineBanner';
import EmailVerificationBanner from './EmailVerificationBanner';
import { externalLinks, openExternalLink } from '../utils/externalLinks';
import { useAutoSupplements } from '../hooks/useAutoSupplements';
import { useHealthKitAutoSync } from '../hooks/useHealthKitAutoSync';
import { useWidgetDeepLink } from '../hooks/useWidgetDeepLink';
import { usePushRegistration } from '../hooks/usePushRegistration';
import { useWidgetDiscovery } from '../hooks/useWidgetDiscovery';
import WidgetDiscoveryDialog from './WidgetDiscoveryDialog';
import TrialPaywall from './onboarding/TrialPaywall';

import { apiService } from '../services/api';
import { trackOnboarding } from '../utils/onboardingTracking';
import { track } from '../utils/track';
import { sendAppleAdsAttribution } from '../utils/attribution';

/** Screens the free-week offer never interrupts. */
const OFFER_SKIP_PATHS = ['/scan', '/upgrade', '/onboarding', '/login', '/signup', '/reset-password'];

interface GlobalLayoutProps {
  children?: React.ReactNode;
}

const navigationItems = [
  { path: '/dashboard', label: 'Dashboard', icon: HomeIcon },
  { path: '/food-log', label: 'Food Log', icon: DocumentTextIcon },
  { path: '/my-foods', label: 'My Foods', icon: BookOpenIcon },
  { path: '/exercise-log', label: 'Exercise', icon: BoltIcon },
  { path: '/fitness', label: 'Fitness', icon: HeartIcon },
  { path: '/supplements', label: 'Supplements', icon: BeakerIcon },
  { path: '/bloodwork', label: 'Bloodwork', icon: ChartBarIcon },
  { path: '/analysis', label: 'Analysis', icon: ChartBarIcon },
  { path: '/friends', label: 'Friends', icon: UserCircleIcon },
  { path: '/coach', label: 'Coach', icon: ChatBubbleBottomCenterTextIcon },
  { path: '/profile', label: 'Profile', icon: UserCircleIcon },
];

export const GlobalLayout: React.FC<GlobalLayoutProps> = ({ children }) => {
  const [mobileDrawerOpen, setMobileDrawerOpen] = React.useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [shouldOpenAI, setShouldOpenAI] = React.useState(false);
  const [isNavigating, setIsNavigating] = React.useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const { unreadCount, markAllAsRead } = useNotificationsContext();


  // Add click-away listener for custom menu
  React.useEffect(() => {
    const handleClickAway = (event: MouseEvent) => {
      if (profileMenuAnchor && !isNavigating) {
        const target = event.target as Node;
        // Check if click is outside the menu and not on the profile button
        if (!profileMenuAnchor.contains(target)) {
          const menuElement = document.querySelector('[data-custom-profile-menu]');
          if (menuElement && !menuElement.contains(target)) {

            setProfileMenuAnchor(null);
          }
        }
      }
    };

    if (profileMenuAnchor) {
      document.addEventListener('mousedown', handleClickAway);
      return () => document.removeEventListener('mousedown', handleClickAway);
    }
  }, [profileMenuAnchor, isNavigating]);

  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  // Close all drawers/overlays on route change to prevent stuck backdrops
  // Also scroll to top — no native scroll restoration in this SPA
  React.useEffect(() => {
    setMobileDrawerOpen(false);
    setNotificationDrawerOpen(false);
    setProfileMenuAnchor(null);
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const { isPro, tier } = useTier();
  useAutoSupplements();
  useHealthKitAutoSync();
  useWidgetDeepLink();
  usePushRegistration();
  const widgetDiscovery = useWidgetDiscovery();

  // Free-week offer: shown once, the first time a non-subscriber opens the app after
  // their Free Pro Day ends (usually day 2). Dismissible — never blocks the app.
  // Guarded by a server flag (trialPaywallShownAt) plus a per-session ref.
  // 2026-10-11: no longer waits for the 3-day setup checklist or 2 days of food —
  // free photo logging runs out within a day or two of the Pro day, so a day-3 offer
  // arrived after the photo wall (Bartek, Poland, hit the wall before the offer).
  const [showTrialPaywall, setShowTrialPaywall] = React.useState(false);
  const paywallEvaluatedRef = React.useRef(false);
  React.useEffect(() => {
    if (paywallEvaluatedRef.current) return;
    if (!user?._id) return;
    // Not in the middle of a scan, a purchase or sign-in.
    if (OFFER_SKIP_PATHS.some((p) => location.pathname.startsWith(p))) return;
    // Only free (non-comped) users
    if (isPro || tier === 'pro') return;
    if ((user as any).trialPaywallShownAt) return;
    // Only after the Free Pro Day has actually ended
    const expiry = (user as any).freeProDayExpiresAt;
    if (!expiry || new Date(expiry).getTime() > Date.now()) return;
    // Only for a Pro day that ended recently — 'your Pro day is over' would be wrong for a months-old account.
    if (Date.now() - new Date(expiry).getTime() > 14 * 24 * 60 * 60 * 1000) return;
    paywallEvaluatedRef.current = true;
    trackOnboarding('paywall_view');
    setShowTrialPaywall(true);
  }, [user, location.pathname, isPro, tier]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissTrialPaywall = React.useCallback(() => {
    setShowTrialPaywall(false);
    apiService.post('/auth/trial-paywall-shown', {}).catch(() => {});
  }, []);

  // Emit one session_start per app session for true DAU / D1-D7-D30 retention
  // (previously retention was proxied through FoodLog only). Guarded per session.
  React.useEffect(() => {
    if (!user?._id) return;
    if (sessionStorage.getItem('ft_session_started')) return;
    sessionStorage.setItem('ft_session_started', '1');
    track('session_start');
    sendAppleAdsAttribution();
  }, [user?._id]);

  // Show a "Back to Setup" bar on activation action pages while a new user is
  // still mid-setup (not graduated, account < 3 days). One tap returns to the
  // activation hub, which auto-refreshes completed steps on focus.
  const ACTIVATION_ACTION_PATHS = ['/dashboard', '/food-log', '/scan', '/supplements', '/bloodwork', '/profile', '/exercise-log'];
  const showSetupBar = (() => {
    if (!user?._id) return false;
    if (localStorage.getItem(`fueltron_activation_graduated_${user._id}`) === 'true') return false;
    const createdMs = (user as any).createdAt ? new Date((user as any).createdAt).getTime() : 0;
    const isNew = createdMs > 0 && (Date.now() - createdMs) < 3 * 24 * 60 * 60 * 1000;
    if (!isNew) return false;
    return ACTIVATION_ACTION_PATHS.some((p) => location.pathname.startsWith(p));
  })();

  const handleCameraAction = () => {
    // Navigate to food log and trigger camera
    navigate('/food-log');
    // Trigger camera functionality after a short delay to ensure page is loaded
    setTimeout(() => {
      const cameraEvent = new CustomEvent('triggerCamera');
      window.dispatchEvent(cameraEvent);
    }, 300);
  };

  const handleVoiceAction = () => {
    // Navigate to food log and trigger voice
    navigate('/food-log');
    // Trigger voice functionality after a short delay to ensure page is loaded
    setTimeout(() => {
      const voiceEvent = new CustomEvent('triggerVoice');
      window.dispatchEvent(voiceEvent);
    }, 300);
  };

  const handleDrawerToggle = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  const handleLogout = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isNavigating) return;
    
    try {
      setIsNavigating(true);
      await logout();
      navigate('/landing');
      setTimeout(() => {
        setProfileMenuAnchor(null);
        setIsNavigating(false);
      }, 100);
    } catch (error) {
      console.error('Logout failed:', error);
      setIsNavigating(false);
    }
  };

  const getCurrentPageTitle = () => {
    const currentItem = navigationItems.find((item) => location.pathname.startsWith(item.path));
    return currentItem?.label || 'FuelTron';
  };

  const drawerContent = (
    <>
      <Toolbar
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: [1],
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            height: '32px'
          }}>
            <img
              src="/FuelTron.png"
              alt="FuelTron Logo"
              style={{
                width: 36,
                height: 36,
                marginRight: '8px',
                borderRadius: '50%',
                filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.4))',
                border: '1px solid rgba(0,224,255,0.2)',
              }}
            />
            <Box
              component="span"
              sx={{
                fontFamily: 'var(--font-mono)',
                fontWeight: 700,
                fontSize: '1.25rem',
                letterSpacing: 2,
                textTransform: 'uppercase',
                background: 'linear-gradient(135deg, #00E0FF, #00E676)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 8px rgba(0,224,255,0.3))',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              FuelTron
            </Box>
          </Box>
        </Box>
      </Toolbar>
      <Box sx={{ px: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, pl: 0.5 }}>
        <Chip
          icon={isPro ? <ProIcon /> : undefined}
          label={`${(tier || 'free').charAt(0).toUpperCase() + (tier || 'free').slice(1)} Tier`}
          color={isPro ? 'success' : 'default'}
          size="small"
          sx={{ fontWeight: 600 }}
        />
        </Box>
        {!isPro && (
          <Button
            component={RouterLink}
            to="/upgrade"
            variant="contained"
            color="primary"
            size="small"
            fullWidth
          >
            Upgrade to Pro
          </Button>
        )}
      </Box>
      <List sx={{ px: 2 }}>
        {navigationItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ display: 'block', mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  if (isMobile && mobileDrawerOpen) {
                    setMobileDrawerOpen(false);
                    // Navigate after backdrop dismisses to prevent stuck blur layer
                    setTimeout(() => navigate(item.path), 200);
                  } else {
                    navigate(item.path);
                  }
                }}
                sx={{
                  color: isActive ? 'var(--color-accent-green)' : 'var(--color-text-secondary)',
                  backgroundColor: isActive ? 'var(--color-bg-accent-subtle)' : 'transparent',
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: 'var(--color-bg-hover)',
                    color: 'var(--color-text-primary)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}>
                  <Icon style={{ width: '24px', height: '24px' }} />
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isActive ? 600 : 500,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <List sx={{ px: 2, mb: 2 }}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              color: 'var(--color-text-secondary)',
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255, 82, 82, 0.1)',
                color: '#ff5252',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit', minWidth: '40px' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Logout"
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 500,
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );

  return (
    <AIBarProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh', background: 'var(--color-bg-primary)' }}>
        <Box component="nav" sx={{ width: { md: 240 }, flexShrink: { md: 0 } }}>
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: 240,
              boxSizing: 'border-box',
              background: 'var(--color-bg-secondary)',
              borderRight: 'none',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileDrawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 240,
              background: 'var(--color-bg-secondary)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          pb: isMobile ? '128px' : 0, // Bottom nav (68px) + AI bar (60px) = 128px
        }}
      >
        <OfflineBanner />
        <EmailVerificationBanner />
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            background: 'rgba(16, 16, 20, 0.8)',
            backdropFilter: 'blur(10px)',
            borderBottom: '1px solid var(--color-border-light)',
            width: { md: 'calc(100% - 240px)' },
            ml: { md: '240px' },
            zIndex: (theme) => theme.zIndex.drawer + 1,
            borderRadius: 0,
          }}
        >
          <Toolbar sx={{ minHeight: '64px' }}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 1, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
            
            {/* FuelTron Branding - Left side */}
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              mr: { xs: 0.5, sm: 1 },
              height: '100%',
              flexShrink: 0, // never let the wordmark clip to "FUELTRO" on narrow phones
              minWidth: { xs: 'auto', sm: 'auto' }
            }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                height: '28px'
              }}>
                <Box
                  component="img"
                  src="/FuelTron.png"
                  alt="FuelTron Logo"
                  sx={{
                    width: 34,
                    height: 34,
                    mr: 0.75,
                    borderRadius: '50%',
                    filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.4))',
                    border: '1px solid rgba(0,224,255,0.2)',
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    letterSpacing: 2,
                    textTransform: 'uppercase',
                    background: 'linear-gradient(135deg, #00E0FF, #00E676)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    textShadow: 'none', // gradient handles the glow via filter
                    filter: 'drop-shadow(0 0 8px rgba(0,224,255,0.3))',
                    display: 'inline-flex',
                    alignItems: 'center',
                    whiteSpace: 'nowrap',
                    pr: '3px', // room for the trailing letter-spacing so the "N" glow isn't clipped
                  }}
                >
                  FuelTron
                </Box>
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />



            {/* Page Title - Show on larger screens as secondary info */}
            <Typography
              variant="body1"
              noWrap
              sx={{
                display: { xs: 'none', lg: 'block' },
                fontWeight: 500,
                color: 'var(--color-text-secondary)',
                mr: 3
              }}
            >
              {getCurrentPageTitle()}
            </Typography>
            
            {/* Right side buttons */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 1.5 },
              height: '100%'
            }}>
              <IconButton
                onClick={() => openExternalLink(externalLinks.featureGuide('header-help'))}
                title="Feature guide"
                sx={{
                  color: 'var(--color-text-secondary)',
                  '&:hover': { color: 'var(--color-text-primary)' },
                  p: isMobile ? 0.5 : 1
                }}
              >
                <HelpOutlineIcon />
              </IconButton>
              <IconButton
                onClick={() => navigate('/analysis')}
                title="Analysis"
                sx={{
                  color: 'var(--color-text-secondary)',
                  '&:hover': { color: 'var(--color-text-primary)' },
                  p: isMobile ? 0.5 : 1
                }}
              >
                <AnalyticsIcon />
              </IconButton>
              <IconButton
                onClick={() => {
                  setNotificationDrawerOpen(true);
                  // Clear badge immediately on drawer open — standard social UX.
                  // "I've seen the list" = "I know what's new."
                  if (unreadCount > 0) markAllAsRead();
                }}
                title="Notifications"
                sx={{
                  color: 'var(--color-text-secondary)',
                  '&:hover': { color: 'var(--color-text-primary)' },
                  p: isMobile ? 0.5 : 1
                }}
              >
                <Badge
                  badgeContent={unreadCount}
                  max={99}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: '#FF1744',
                      color: '#fff',
                      fontSize: '0.6rem',
                      fontWeight: 700,
                      minWidth: 18,
                      height: 18,
                    },
                  }}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
              <IconButton onClick={(e) => {
                setProfileMenuAnchor(profileMenuAnchor ? null : e.currentTarget);
              }} sx={{ p: isMobile ? 0.5 : 1 }}>
                <Avatar
                  src={(user as any)?.profilePicture?.url}
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: 'var(--accent-blue)',
                    color: 'var(--text-on-accent)',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    border: '1.5px solid var(--accent-blue)',
                    '&:hover': {
                      backgroundColor: '#00b3cc',
                      border: '1.5px solid #00b3cc',
                      transform: 'scale(1.05)',
                    },
                    transition: 'all var(--transition-fast)',
                  }}
                >
                  {user?.firstName?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
        
        {/* Centered Content Area */}
        <Box
            component="div"
            sx={{
                flexGrow: 1,
                pt: { xs: 1, sm: 1.5, md: 2 },
                px: { xs: 1, sm: 3, md: 4 },
                pb: { xs: 2, sm: 3, md: 4 },
                mt: { xs: '56px', sm: '64px' }, // Header height
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                width: '100%',
            }}
            >
            <Box sx={{ width: '100%', maxWidth: '1200px' }}>
                {showSetupBar && (
                  <Box
                    onClick={() => navigate('/activation')}
                    className="icon-text-row"
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      mt: { xs: 1.5, sm: 2 }, mb: 2, px: 2, py: 1.4,
                      borderRadius: '10px', cursor: 'pointer',
                      border: '1px solid rgba(0,224,255,0.55)',
                      background: 'linear-gradient(90deg, rgba(0,224,255,0.16), rgba(0,230,118,0.10))',
                      boxShadow: '0 0 16px rgba(0,224,255,0.2)',
                      '&:hover': { background: 'linear-gradient(90deg, rgba(0,224,255,0.24), rgba(0,230,118,0.16))' },
                    }}
                  >
                    <ArrowBackIcon sx={{ color: '#00E0FF', fontSize: 20, filter: 'drop-shadow(0 0 6px rgba(0,224,255,0.5))' }} />
                    <Typography sx={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', fontWeight: 700, color: '#00E0FF', letterSpacing: 0.8 }}>
                      BACK TO SETUP
                    </Typography>
                  </Box>
                )}
                {children || <Outlet />}
            </Box>
        </Box>
      </Box>

      {/* Custom positioned menu - only show when profileMenuAnchor exists */}
      {Boolean(profileMenuAnchor) && (
        <Box
          data-custom-profile-menu
          sx={{
            position: 'fixed',
            top: profileMenuAnchor ? `${profileMenuAnchor.getBoundingClientRect().bottom + 8}px` : '64px',
            right: profileMenuAnchor ? `${window.innerWidth - profileMenuAnchor.getBoundingClientRect().right}px` : '16px',
            zIndex: 9999,
            backgroundColor: 'var(--color-bg-card)',
            border: '1px solid var(--color-border-light)',
            borderRadius: '8px',
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(20px)',
          }}
        >
          <List sx={{ p: 1 }}>
            {[
              { label: 'Profile & Settings', icon: <PersonIcon fontSize="small" />, path: '/profile' },
              { label: 'Privacy Policy', icon: <PolicyIcon fontSize="small" />, path: '/privacy' },
              { label: 'Terms of Service', icon: <TermsIcon fontSize="small" />, path: '/terms' },
            ].map(item => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (isNavigating) return;
                    setIsNavigating(true);
                    navigate(item.path);
                    setTimeout(() => { setProfileMenuAnchor(null); setIsNavigating(false); }, 100);
                  }}
                  sx={{ borderRadius: '6px', mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>{item.icon}</ListItemIcon>
                  <ListItemText>{item.label}</ListItemText>
                </ListItemButton>
              </ListItem>
            ))}
            <Divider sx={{ my: 1 }} />
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{ borderRadius: '6px' }}
              >
                <ListItemIcon sx={{ minWidth: '36px' }}>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}
      
      {isMobile && <BottomNavigation />}

      {/* Invisible tutorial anchors for desktop to ensure Joyride steps exist */}
      <Box sx={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, pointerEvents: 'none', display: { xs: 'none', md: 'block' } }}>
        <span data-tour="bottom-nav" />
        <span data-tour="supplements" />
        <span data-tour="bloodwork" />
        <span data-tour="analysis" />
      </Box>

      {/* Persistent AI Input Bar */}
      <PersistentAIBar />

      {/* Notification Center Drawer */}
      <NotificationCenter
        open={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
      />

      {/* Post-trial soft paywall — dismissible, shown once after Free Pro Day ends */}
      {showTrialPaywall && (
        <TrialPaywall
          firstName={user?.firstName}
          onStartTrial={() => { dismissTrialPaywall(); navigate('/upgrade'); }}
          onSkip={dismissTrialPaywall}
        />
      )}

      {/* iOS widget + Siri discovery (no-op on non-iOS) */}
      <WidgetDiscoveryDialog
        open={widgetDiscovery.widgetDialogOpen}
        onClose={widgetDiscovery.closeWidgetDialog}
      />
      <Snackbar
        open={widgetDiscovery.siriTipOpen}
        onClose={widgetDiscovery.dismissSiriTip}
        autoHideDuration={9000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 140, lg: 80 } }}
      >
        <Box
          onClick={widgetDiscovery.dismissSiriTip}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.25,
            background: 'rgba(10,14,20,0.95)',
            border: '1px solid rgba(206,147,216,0.4)',
            borderRadius: 1.5,
            boxShadow: '0 4px 16px rgba(0,0,0,0.5), 0 0 16px rgba(206,147,216,0.2)',
            cursor: 'pointer',
            maxWidth: 360,
          }}
        >
          <Box sx={{ fontSize: 18, lineHeight: 1 }}>💡</Box>
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                fontWeight: 700,
                color: '#CE93D8',
                letterSpacing: 1.2,
                mb: 0.25,
              }}
            >
              SIRI TIP
            </Box>
            <Box sx={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.4 }}>
              Try saying <strong>"Hey Siri, scan a meal with FuelTron"</strong>
            </Box>
          </Box>
        </Box>
      </Snackbar>
    </Box>
    </AIBarProvider>
  );
};